import type {AnalyticsPlugin} from 'analytics';

import {allowlistedCookies, filterAllowedCookies} from '../shared/cookies';
import type {AnalyticsContext, NormalizedRequestContext, ServiceConfig} from '../shared/types';
import {SDK_NAME, SDK_VERSION} from '../version';

export default function contextExtractor(
  config: {service: ServiceConfig},
  getAnalyticsContext: () => AnalyticsContext | undefined,
): AnalyticsPlugin {
  return {
    name: 'contextExtractor',
    'page:rudderstackEvents': ({payload}: {payload: Record<string, unknown>}) =>
      enrichPayload(config, payload, getAnalyticsContext()),
    'track:rudderstackEvents': ({payload}: {payload: Record<string, unknown>}) =>
      enrichPayload(config, payload, getAnalyticsContext()),
  } as AnalyticsPlugin;
}

function buildCloudflareContext(
  analyticsContext: AnalyticsContext | undefined,
): Record<string, string | null> {
  const extractedHeaders = extractRequestHeaders(analyticsContext);
  const {headers} = extractedHeaders ?? {};

  return {
    botScore: headers?.cfBotScore ?? null,
    rayId: headers?.cfRay ?? null,
    verifiedBot: headers?.cfVerifiedBot ?? null,
  };
}

function extractRequestHeaders(
  analyticsContext: AnalyticsContext | undefined,
): NormalizedRequestContext {
  if (!analyticsContext) return {};

  const headers = analyticsContext.request?.headers;

  if (!headers) return {};

  const header = (name: string): string | null => {
    const v = headers?.[name];
    return (Array.isArray(v) ? v[0] : v) ?? null;
  };

  return {
    headers: {
      cfBotScore: header('cf-bot-score'),
      cfConnectingIp: header('cf-connecting-ip'),
      cfRay: header('cf-ray'),
      cfVerifiedBot: header('cf-verified-bot'),
      xForwardedFor: header('x-forwarded-for'),
      xRealIp: header('x-real-ip'),
      xRequestId: header('x-request-id'),
    },
  };
}

function extractRequestCookies(
  analyticsContext: AnalyticsContext | undefined,
): Record<string, string> {
  // SSR: cookies provided via analyticsContext (from extractAnalyticsContext(req))
  if (analyticsContext?.request?.cookies) {
    return filterAllowedCookies(analyticsContext.request.cookies);
  }

  // CSR / vanilla JS: read from document.cookie in browser environments
  if (typeof document !== 'undefined') {
    return allowlistedCookies(document.cookie);
  }

  return {};
}

function buildRequestContext(
  analyticsContext: AnalyticsContext | undefined,
  existingRequestContext: Record<string, unknown>,
): Record<string, unknown> {
  const analyticsRequest = analyticsContext?.request;
  const cookies = extractRequestCookies(analyticsContext);
  const requestHeaders = extractRequestHeaders(analyticsContext)?.headers;
  const extractedHeaders: Record<string, string | null> = {
    cfConnectingIp: requestHeaders?.cfConnectingIp ?? null,
    xForwardedFor: requestHeaders?.xForwardedFor ?? null,
    xRealIp: requestHeaders?.xRealIp ?? null,
    xRequestId: requestHeaders?.xRequestId ?? null,
  };
  const mergedRequestContext: Record<string, unknown> = {...existingRequestContext};
  const requestEntries: Record<string, unknown> = {
    headers: extractedHeaders,
    cookies,
    ip: analyticsRequest?.ip ?? null,
    method: analyticsRequest?.method ?? null,
    remoteIp: analyticsRequest?.remoteIp ?? null,
    userAgent: analyticsRequest?.userAgent ?? null,
  };

  return {...mergedRequestContext, ...requestEntries};
}

function enrichPayload(
  config: {service: ServiceConfig},
  payload: Record<string, unknown>,
  analyticsContext: AnalyticsContext | undefined,
): Record<string, unknown> {
  const enrichedContext: Record<string, unknown> = {
    ...(payload.context ?? {}),
    eventClient: {name: SDK_NAME, version: SDK_VERSION},
    service: {
      name: config.service.name,
      version: config.service.version ?? undefined,
    },
  };
  const requestContext = buildRequestContext(
    analyticsContext,
    (enrichedContext.request as Record<string, unknown>) ?? {},
  );
  enrichedContext.cloudflare = buildCloudflareContext(analyticsContext);
  enrichedContext.request = requestContext;

  return {
    ...payload,
    context: enrichedContext,
  };
}
